import { ChangeDetectorRef, Component, EventEmitter, inject, InjectionToken, Input, Output } from "@angular/core";
import { AgreementBundle, AgreementBundleType } from "../../terms-and-conditions/entities/terms-and-conditions.model";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Tradable } from "@models/interfaces/tradable";

export const DYNAMIC_TERMS_TOKEN = new InjectionToken<TermsAndConditionsUpdatesComponent>(
	"terms-and-conditions_updates"
);

@Component({
	selector: "terms-and-conditions-updates",
	templateUrl: "./terms-and-conditions-updates.component.html",
	styleUrls: ["./terms-and-conditions-updates.component.scss"],
	providers: [
		{
			provide: DYNAMIC_TERMS_TOKEN,
			useExisting: TermsAndConditionsUpdatesComponent,
			multi: true
		}
	],
	standalone: false
})
export class TermsAndConditionsUpdatesComponent {
	private fb = inject(FormBuilder);
	private cdr = inject(ChangeDetectorRef);

	private _bundles: AgreementBundle[] = [];

	@Input()
	set bundles(value: AgreementBundle[]) {
		this._bundles = value;
		this.updateForm();
	}
	@Input() lightColor = false;
	@Input() tradable: Tradable = null;
	@Input() toReplaceInLink = "%asset_legal_documents%";
	@Input() urlLeverageToken = "";
	@Input() hasNoticeImplicitConsent: boolean;

	get bundles(): AgreementBundle[] {
		return this._bundles;
	}

	@Output() onTriggerChange = new EventEmitter<boolean>(); // an extra trigger needed for the change detection
	@Output() onTermsBuilding = new EventEmitter<void>();
	termsAndConditionsForm: FormGroup | undefined;
	// wiggles state
	wiggleTerm = false;

	private updateForm(): void {
		if (this.bundles) {
			this.termsAndConditionsForm = this.fb.group(this.termsArrayBuilderHelper(this.bundles));
			this.onTriggerChange.emit(true);
			this.onTermsBuilding.emit();
		}
	}

	private termsArrayBuilderHelper(bundles: AgreementBundle[]): { [key: string]: FormControl } {
		return bundles?.reduce(
			(acc, bundle) => {
				const _validators = bundle.type !== "NOTICE" && bundle.mandatory ? [Validators.requiredTrue] : [];
				const preselected: boolean = this.hasNoticeImplicitConsent
					? bundle.type === AgreementBundleType.NOTICE
					: false;

				acc[bundle.agreement_bundle_version_id] = new FormControl(preselected, _validators);
				return acc;
			},
			{} as { [key: string]: FormControl }
		);
	}

	// wiggle terms
	wiggleIt(): void {
		this.wiggleTerm = true;
		this.cdr.markForCheck();

		setTimeout(() => {
			this.wiggleTerm = false;
			this.cdr.markForCheck();
		}, 1000);
	}

	resetToggles(): void {
		this.termsAndConditionsForm?.reset();
	}

	disableToggles(): void {
		// disable toggles after 1 second because the form gets reset and updated
		setTimeout(() => {
			this.termsAndConditionsForm?.disable();
		}, 1000);
	}

	enableToggles(): void {
		this.termsAndConditionsForm?.enable();
	}

	toggle(event: MouseEvent, bundleId: string): void {
		const target = event.target as HTMLElement;
		if (target.tagName.toLowerCase() === "a") {
			target.setAttribute("target", "_blank");
			return;
		}
		const formControl = this.termsAndConditionsForm.get(bundleId);
		const actualValue = formControl.value;
		formControl.setValue(!actualValue);
	}

	public isTypeAgreement(bundle: AgreementBundle): boolean {
		return bundle.type === AgreementBundleType.AGREEMENT;
	}
}
